import { BikeState, steerInputFor } from './bike';
import { featureLength, isJump } from './features';
import type { Level } from './level';
import { CenterLine } from './centerline';
import { BikeConfig } from './config';
import { InputFrame } from './input';
import { clamp, rotateInv, vec } from './math';

export interface BotOptions {
  cruise: number;          // m/s on open track
  lateralAccel: number;    // m/s^2 allowed in corners
  brakeDecel: number;      // m/s^2 assumed when planning braking
  lookahead: number;       // m
  lookaheadPerSpeed: number; // s
  speedLimit?: (s: number) => number; // extra cap by arc length (jumps, logs)
}

/** Nose attitude (rad) the bot holds in the air: a little down, to match a landing slope. */
export const BOT_AIR_PITCH = -0.05;

/** Speed cap by arc length that rides every jump of a level at its reference speed from 30 m before its ramp. */
export function jumpSpeedLimit(level: Level, lipOnly = false): (s: number) => number {
  const line = level.line;
  const jumps = level.features.filter(isJump);
  return (s: number) => {
    const u = line ? line.wrap(s) : s;
    // lipOnly (the rider's assist): only the run-up to the lip; the bot also holds it through the landing.
    for (const f of jumps) if (u > f.s - (lipOnly ? 15 : 30) && u < f.s + (lipOnly ? 2 : featureLength(f))) return f.refSpeed;
    return Infinity;
  };
}

export const DEFAULT_BOT: BotOptions = { cruise: 12, lateralAccel: 4, brakeDecel: 3, lookahead: 3, lookaheadPerSpeed: 0.5 };

/** Pure-pursuit rider for tests: steers at a point ahead on the centreline and caps speed by curvature. */
export class PathBot {
  s: number;
  distance = 0;
  lateral = 0;

  constructor(readonly line: CenterLine, readonly cfg: BikeConfig, readonly opts: BotOptions = DEFAULT_BOT, startS = 0) {
    this.s = line.wrap(startS);
  }

  targetSpeed(s: number, v: number): number {
    const o = this.opts;
    let target = Math.min(o.cruise, o.speedLimit?.(s) ?? Infinity);
    const horizon = (v * v) / (2 * o.brakeDecel) + 10;
    for (let x = 0; x <= horizon; x += 2) {
      const k = Math.abs(this.line.curvatureAt(s + x));
      let lim = k > 1e-4 ? Math.sqrt(o.lateralAccel / k) : Infinity;
      if (o.speedLimit) lim = Math.min(lim, o.speedLimit(s + x));
      target = Math.min(target, Math.sqrt(lim * lim + 2 * o.brakeDecel * x));
    }
    return target;
  }

  input(bike: BikeState): InputFrame {
    const p = bike.position;
    const c = this.line.closest(p.x, p.z, this.s, 30);
    let ds = c.s - this.s;
    const L = this.line.length;
    if (ds > L / 2) ds -= L; else if (ds < -L / 2) ds += L;
    this.distance += ds;
    this.s = c.s;
    this.lateral = c.d;

    const v = bike.telemetry.speed;
    const ld = this.opts.lookahead + this.opts.lookaheadPerSpeed * Math.max(0, v);
    const tp = this.line.pointAt(c.s + ld);
    const local = rotateInv(bike.orientation, vec(tp.x - p.x, 0, tp.z - p.z));
    const alpha = Math.atan2(local.x, local.z);
    const kappa = (2 * Math.sin(alpha)) / ld;
    const angle = Math.atan(kappa * this.cfg.wheelbase);
    const steer = steerInputFor(angle, v, this.cfg);

    if (bike.telemetry.airborne) {
      // In the air: steady part throttle, more if the nose drops, the rear brake if it climbs, like a rider would.
      const e = bike.telemetry.pitch - BOT_AIR_PITCH;
      return { throttle: e < -0.05 ? 1 : e < 0.05 ? 0.3 : 0, brake: 0, rearBrake: e > 0.15 ? 0.8 : 0, steer: 0, body: 0, standing: true };
    }
    const target = this.targetSpeed(c.s, v);
    const throttle = clamp((target - v) * 1.2 + 0.15, 0, 1); // + a little to hold the speed against drag
    const brake = v > target + 0.5 ? clamp((v - target) * 0.3, 0, 1) : 0;
    return { throttle, brake, steer, body: 0, standing: true };
  }
}
